/**
 * LRN（Land Run）課題の幾何プリミティブと戦略プランナー。
 *
 * 友人のTaskViewアプリ側の実装方針をまとめた説明ファイル
 * （`LRN_3DT_implementation_spec.md`、リポジトリには同梱していない
 * パイロット提供の資料）の計算式をそのまま移植したもの。参考:
 *   LRN: https://ukcompetitionballooning.com/index.php/land-run/
 *
 * ★★ ここで出す数字（面積・座標・推奨タイミング）は**飛行支援のための
 *   参考値**であって公式スコアではない。公式の採点は主催者の採点ソフトが
 *   行う。UI では必ず「参考値」と分かる形で出すこと（airspace.ts と
 *   同じ方針）。
 *
 * 実際の飛行軌跡からA/B/Cを自動選んで公式風にスコア化する
 * `scoreLrn(task, track)`は対象外（点群からの最大面積三角形探索という
 * 別の幾何アルゴリズムが必要で、参照した資料にも記述が無いため）。
 * ここにあるのは「これから飛ぶならどうすると良いか」を提案する
 * 戦略プランナー（`planLrnTime`/`planLrnCircle`）のみ。
 */

/** UTM平面上の1点（東距・北距 [m]）。ゾーンは呼び出し側が単一ゾーン内と保証する */
export interface LrnPoint {
  e: number;
  n: number;
}

/**
 * 風の1層。dirToTrueDeg は「気球が流される方向」の真方位
 * （風が「吹いてくる」方向でも磁方位でもない。WindSample.dir と同じ規約）
 */
export interface LrnWindLayer {
  altFt: number;
  dirToTrueDeg: number;
  speedMps: number;
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** 層の風ベクトル成分（東=vx・北=vy [m/s]） */
function layerVector(layer: LrnWindLayer): { vx: number; vy: number } {
  const rad = layer.dirToTrueDeg * DEG_TO_RAD;
  return { vx: layer.speedMps * Math.sin(rad), vy: layer.speedMps * Math.cos(rad) };
}

/**
 * 指定高度の風を、プロファイルの隣接する2層からベクトル成分（vx/vy）で
 * 線形補間する。角度のまま平均・補間しないこと（gridWindSamples と同じ
 * 規律。350°と10°を角度のまま混ぜると180°になり真逆になる）。
 *
 * プロファイルの高度範囲外（最低層より低い・最高層より高い）はnull。
 * ちょうど一致する層があればそれをそのまま返す。
 */
export function windLayerAtAlt(profile: readonly LrnWindLayer[], altFt: number): LrnWindLayer | null {
  if (profile.length === 0) return null;
  const sorted = [...profile].sort((a, b) => a.altFt - b.altFt);

  if (altFt < sorted[0].altFt || altFt > sorted[sorted.length - 1].altFt) return null;

  const exact = sorted.find((l) => l.altFt === altFt);
  if (exact) return exact;

  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (altFt < lo.altFt || altFt > hi.altFt) continue;
    const f = (altFt - lo.altFt) / (hi.altFt - lo.altFt);
    const loVec = layerVector(lo);
    const hiVec = layerVector(hi);
    const vx = loVec.vx + (hiVec.vx - loVec.vx) * f;
    const vy = loVec.vy + (hiVec.vy - loVec.vy) * f;
    return {
      altFt,
      dirToTrueDeg: (Math.atan2(vx, vy) * RAD_TO_DEG + 360) % 360,
      speedMps: Math.hypot(vx, vy),
    };
  }
  return null;
}

/** 指定した層の風に乗って dtSec 秒飛んだあとの位置 */
export function advanceAlongLayer(p: LrnPoint, layer: LrnWindLayer, dtSec: number): LrnPoint {
  const { vx, vy } = layerVector(layer);
  return { e: p.e + vx * dtSec, n: p.n + vy * dtSec };
}

/**
 * 三角形 A/B/C の面積 [km²]（シューレース公式）。大きいほど良い結果。
 * `area_m2 = |(B.E-A.E)*(C.N-A.N) - (B.N-A.N)*(C.E-A.E)| / 2`
 */
export function lrnAreaKm2(a: LrnPoint, b: LrnPoint, c: LrnPoint): number {
  const areaM2 = Math.abs((b.e - a.e) * (c.n - a.n) - (b.n - a.n) * (c.e - a.e)) / 2;
  return areaM2 / 1_000_000;
}

/**
 * 開始点`start`から方位`dirToTrueDeg`へ直進したときの、円`(center, radiusM)`
 * との脱出点。`start`は円周上にある前提。円と交わらなければnull。
 */
export function rayCircleExit(
  start: LrnPoint,
  dirToTrueDeg: number,
  center: LrnPoint,
  radiusM: number,
): LrnPoint | null {
  const rad = dirToTrueDeg * DEG_TO_RAD;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const dx = start.e - center.e;
  const dy = start.n - center.n;

  const b = 2 * (dx * ux + dy * uy);
  const c = dx * dx + dy * dy - radiusM * radiusM;
  const disc = b * b - 4 * c;
  if (disc < 0) return null;

  const root = Math.sqrt(disc);
  const hits = [(-b - root) / 2, (-b + root) / 2].filter((t) => t > 1e-6);
  if (hits.length === 0) return null;

  const t = Math.max(...hits);
  return { e: start.e + ux * t, n: start.n + uy * t };
}

/**
 * 2層の組み合わせの粗い評点（大きいほど良い候補）。
 * `score = speed1*speed2*|sin(dir1-dir2)|` — 風速が大きく、向きが直交に
 * 近いほど大きな三角形を作りやすいという粗い目安（TaskView側の実装方針
 * の層選定ヒューリスティックをそのまま移植）。
 */
export function lrnLayerPairScore(a: LrnWindLayer, b: LrnWindLayer): number {
  const deltaRad = (a.dirToTrueDeg - b.dirToTrueDeg) * DEG_TO_RAD;
  return a.speedMps * b.speedMps * Math.abs(Math.sin(deltaRad));
}

// ---------------------------------------------------------------------------
// 戦略プランナー
// ---------------------------------------------------------------------------

interface LrnTaskBase {
  /** タスク制限時間 [秒] */
  limitSec: number;
  altMinFt: number;
  altMaxFt: number;
  /** 上昇速度 [m/s] */
  climbMps: number;
  /** 降下速度 [m/s] */
  descentMps: number;
}

export interface LrnTimeTask extends LrnTaskBase {
  mode: 'time';
  start: LrnPoint;
}

/** ★ circleモードは開始点を持たない。円周上の候補点を総当たりするため */
export interface LrnCircleTask extends LrnTaskBase {
  mode: 'circle';
  circle: { center: LrnPoint; radiusM: number };
}

export type LrnTask = LrnTimeTask | LrnCircleTask;

export interface LrnLeg {
  layer: LrnWindLayer;
  from: LrnPoint;
  to: LrnPoint;
  fromSec: number;
  toSec: number;
}

export interface LrnPlan {
  legs: [LrnLeg, LrnLeg];
  triangle: { a: LrnPoint; b: LrnPoint; c: LrnPoint };
  areaKm2: number;
}

export interface LrnPlanOptions {
  /** circleモードで試す円周上の開始候補点の数。既定72（5°刻み） */
  candidateCount?: number;
}

const DEFAULT_CANDIDATE_COUNT = 72;

function distance(a: LrnPoint, b: LrnPoint): number {
  return Math.hypot(b.e - a.e, b.n - a.n);
}

/**
 * 高度帯（altMinFt〜altMaxFt）に入る層だけを候補にする。
 * `three-dt-strategy.ts`の候補層絞り込みでも同じ関数を使う
 */
export function layersInBand(profile: readonly LrnWindLayer[], altMinFt: number, altMaxFt: number): LrnWindLayer[] {
  return profile.filter((l) => l.altFt >= altMinFt && l.altFt <= altMaxFt);
}

/**
 * 高度altFtAからaltFtBへの移行にかかる時間 [秒]（上昇・降下で速度が違う）。
 * `three-dt-strategy.ts`の高度切替でも同じ関数を使う
 */
export function transitionSecOf(altFtA: number, altFtB: number, climbMps: number, descentMps: number): number {
  const dzM = Math.abs(altFtB - altFtA) * 0.3048;
  const rateMps = altFtB >= altFtA ? climbMps : descentMps;
  return dzM / rateMps;
}

/**
 * 層A・Bの風ベクトルの平均で、移行区間（高度を切り替えている最中）の
 * 移動を近似する。実際の移行経路は単純な直線ではないはずだが、詳細な
 * 高度—時間対応が資料に無い以上の簡略化。dtSec=0なら移動しない。
 * `three-dt-strategy.ts`の高度切替でも同じ近似を使う
 */
export function advanceThroughTransition(p: LrnPoint, a: LrnWindLayer, b: LrnWindLayer, dtSec: number): LrnPoint {
  if (dtSec <= 0) return p;
  const va = layerVector(a);
  const vb = layerVector(b);
  const vx = (va.vx + vb.vx) / 2;
  const vy = (va.vy + vb.vy) / 2;
  return { e: p.e + vx * dtSec, n: p.n + vy * dtSec };
}

/** 候補層から全ての順序付きペア（(A,B)と(B,A)は別候補）を作る */
export function orderedLayerPairs(layers: readonly LrnWindLayer[]): Array<[LrnWindLayer, LrnWindLayer]> {
  const pairs: Array<[LrnWindLayer, LrnWindLayer]> = [];
  for (const a of layers) {
    for (const b of layers) {
      if (a !== b) pairs.push([a, b]);
    }
  }
  return pairs;
}

/**
 * 時間制限モードのLRN戦略を提案する。
 *
 * 候補となる層の全ての順序付きペアを実際にシミュレートし、面積が最大の
 * ものを採用する（ヒューリスティックな評点による事前絞り込みはしない。
 * 現実的な層数は一桁〜十数件程度で全探索のコストは軽く、評点は実際の
 * 面積と完全には相関しないため）。
 *
 * 残り時間は「A層で待機→A→B移行→B層で待機」の2区間に均等分割する
 * （資料も模式のみでスケジューリングの厳密解は与えていないため、
 * 最も素直な近似として採用した簡略化）。
 *
 * 成立するペアが1つも無ければnull（例: どの層への移行も制限時間を
 * 超えてしまう、候補層が2つ未満）。
 */
export function planLrnTime(
  task: LrnTimeTask,
  profile: readonly LrnWindLayer[],
  _options: LrnPlanOptions = {},
): LrnPlan | null {
  let best: LrnPlan | null = null;
  for (const [a, b] of orderedLayerPairs(layersInBand(profile, task.altMinFt, task.altMaxFt))) {
    const plan = planLrnTimePair(task, a, b);
    if (plan && (!best || plan.areaKm2 > best.areaKm2)) best = plan;
  }
  return best;
}

/**
 * 時間制限モードで、層のペアを1組だけ評価する。
 *
 * `planLrnTime` の探索1回ぶんを切り出したもの。アドバイザー
 * （`lrn-advisor.ts`）が「採用しなかった候補も含めて並べる」ために
 * 同じ計算を必要とするので、探索ロジックを二重に持たないよう共有する。
 * 制限時間内に移行が収まらなければ null。
 */
export function planLrnTimePair(
  task: LrnTimeTask,
  a: LrnWindLayer,
  b: LrnWindLayer,
): LrnPlan | null {
  const transitionSec = transitionSecOf(a.altFt, b.altFt, task.climbMps, task.descentMps);
  if (transitionSec >= task.limitSec) return null;

  const legSec = (task.limitSec - transitionSec) / 2;
  const legAEnd = advanceAlongLayer(task.start, a, legSec);
  const transitionEnd = advanceThroughTransition(legAEnd, a, b, transitionSec);
  const legBEnd = advanceAlongLayer(transitionEnd, b, legSec);

  return {
    legs: [
      { layer: a, from: task.start, to: legAEnd, fromSec: 0, toSec: legSec },
      {
        layer: b,
        from: transitionEnd,
        to: legBEnd,
        fromSec: legSec + transitionSec,
        toSec: legSec + transitionSec + legSec,
      },
    ],
    triangle: { a: task.start, b: legAEnd, c: legBEnd },
    areaKm2: lrnAreaKm2(task.start, legAEnd, legBEnd),
  };
}

/**
 * 円モードのLRN戦略を提案する。
 *
 * 円周上に等間隔の開始候補点を置き、各候補×層の全順序ペアの組み合わせで
 * 「層Aで走って円を出た点B、層Bへ移行して円を出た点C」をシミュレートし、
 * 制限時間内に収まり面積が最大のものを採用する（資料item 7のアルゴリズム
 * をそのまま実装）。
 */
export function planLrnCircle(
  task: LrnCircleTask,
  profile: readonly LrnWindLayer[],
  options: LrnPlanOptions = {},
): LrnPlan | null {
  let best: LrnPlan | null = null;
  for (const [a, b] of orderedLayerPairs(layersInBand(profile, task.altMinFt, task.altMaxFt))) {
    const plan = planLrnCirclePair(task, a, b, options);
    if (plan && (!best || plan.areaKm2 > best.areaKm2)) best = plan;
  }
  return best;
}

/**
 * 円モードで、層のペアを1組だけ評価する（円周上の開始候補点は総当たりし、
 * そのペアでの最良を返す）。
 *
 * `planLrnCircle` の探索を層ペア単位で切り出したもの。理由は
 * `planLrnTimePair` と同じで、アドバイザーと探索ロジックを共有するため。
 * 制限時間内に収まる組み合わせが1つも無ければ null。
 */
export function planLrnCirclePair(
  task: LrnCircleTask,
  layerA: LrnWindLayer,
  layerB: LrnWindLayer,
  options: LrnPlanOptions = {},
): LrnPlan | null {
  const candidateCount = options.candidateCount ?? DEFAULT_CANDIDATE_COUNT;
  const { center, radiusM } = task.circle;
  let best: LrnPlan | null = null;

  for (let i = 0; i < candidateCount; i++) {
    const theta = ((360 / candidateCount) * i * Math.PI) / 180;
    const a: LrnPoint = { e: center.e + radiusM * Math.sin(theta), n: center.n + radiusM * Math.cos(theta) };

    const b = rayCircleExit(a, layerA.dirToTrueDeg, center, radiusM);
    if (!b) continue;
    const tAB = distance(a, b) / layerA.speedMps;

    const transitionSec = transitionSecOf(layerA.altFt, layerB.altFt, task.climbMps, task.descentMps);

    const c = rayCircleExit(b, layerB.dirToTrueDeg, center, radiusM);
    if (!c) continue;
    const tBC = distance(b, c) / layerB.speedMps;

    if (tAB + transitionSec + tBC > task.limitSec) continue;

    const areaKm2 = lrnAreaKm2(a, b, c);
    if (!best || areaKm2 > best.areaKm2) {
      best = {
        legs: [
          { layer: layerA, from: a, to: b, fromSec: 0, toSec: tAB },
          { layer: layerB, from: b, to: c, fromSec: tAB + transitionSec, toSec: tAB + transitionSec + tBC },
        ],
        triangle: { a, b, c },
        areaKm2,
      };
    }
  }

  return best;
}
